import { useEffect, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Crop as CropIcon } from "@phosphor-icons/react";

export default function PhotoCropper({ file, onDone }) {
  const [crop, setCrop] = useState();
  const [src, setSrc] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!file) {
      setSrc(null);
      return;
    }
    setCrop(undefined);
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const confirmCrop = () => {
    const img = imgRef.current;
    if (!img || !crop?.width || !crop?.height) {
      onDone(file);
      return;
    }
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(crop.width * scaleX);
    canvas.height = Math.round(crop.height * scaleY);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      img,
      crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY,
      0, 0, canvas.width, canvas.height
    );
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    canvas.toBlob((blob) => onDone(new File([blob], file.name, { type })), type, 0.92);
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onDone(null); }}>
      <DialogContent className="max-w-3xl bg-card border-border" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CropIcon size={18} /> Crop Photo
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Drag on the image to select the area to keep, or use the original as-is.
        </p>
        <div className="flex justify-center bg-black/20 border border-border p-2 max-h-[60vh] overflow-auto">
          {src && (
            <ReactCrop crop={crop} onChange={(c) => setCrop(c)}>
              <img ref={imgRef} src={src} alt="crop preview" style={{ maxHeight: "55vh", maxWidth: "100%" }} />
            </ReactCrop>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button data-testid="crop-cancel-btn" variant="outline" onClick={() => onDone(null)}>Cancel</Button>
          <Button data-testid="crop-use-original-btn" variant="outline" onClick={() => onDone(file)}>Use Original</Button>
          <Button data-testid="crop-confirm-btn" onClick={confirmCrop} disabled={!crop?.width}
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
            Crop &amp; Attach
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
